import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CHU_PIECES, CHU_PROMOTES_TO, CHU_SIZE,
  type ChuShogiMove, type ChuShogiState, type ChuSquare, chuPotentialMoves, chuFirstSteps, chuMovesFrom,
  chuMovesTo, chuPositionKey, chuSquareLabel, type Side,sameChuMove, sameChuSquare,
} from '../../games/chu-shogi';
import { getEngine } from '../../wasm';
import { BoardCoords } from './BoardCoords';
import { ShogiKoma } from './ShogiKoma';
import './chu-shogi.css';

interface Props {
  state: ChuShogiState;
  legal?: ChuShogiMove[];
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (move: ChuShogiMove) => void;
  mini?: boolean;
  lastMove?: ChuShogiMove;
  staticView?: boolean;
  suppressTurnStatus?: boolean;
  tutorialHighlight?: ChuSquare[];
}

interface Selection {
  from: ChuSquare;
  via: ChuSquare | null;
  position: string;
  moves: ChuShogiMove[];
  potentialMoves: ChuShogiMove[];
}

export function ChuShogiBoard({ state, legal = [], yourSide, myTurnAllowed, onMove, mini,
  lastMove, staticView, suppressTurnStatus = false, tutorialHighlight }: Props) {
  const isMyTurn = myTurnAllowed ?? (yourSide != null && yourSide === state.side);
  const canMove = !mini && !staticView && isMyTurn && state.outcome == null && onMove != null;
  const canInspect = !mini;
  const position = chuPositionKey(state);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [promotion, setPromotion] = useState<{ moves: ChuShogiMove[]; position: string } | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [controlsHeight, setControlsHeight] = useState(0);
  // 小画面で状態行が折り返しても、Playの縦budgetから実測分を確保する。
  useLayoutEffect(() => {
    const controls = controlsRef.current;
    if (!controls || mini || staticView || suppressTurnStatus) { setControlsHeight(0); return; }
    const measure = () => setControlsHeight(Math.ceil(controls.getBoundingClientRect().height));
    const observer = new ResizeObserver(measure);
    observer.observe(controls);
    measure();
    return () => observer.disconnect();
  }, [mini, staticView, suppressTurnStatus]);
  // 別盤の broadcast では残し、この盤の着手・終局・手番変更で取り消す。
  useEffect(() => {
    setSelection((current) => canInspect && current?.position === position ? current : null);
  }, [position, canInspect]);
  useEffect(() => {
    setPromotion((current) => canMove && current?.position === position ? current : null);
  }, [position, canMove]);
  const selected = canInspect && selection?.position === position ? selection : null;
  const pending = canMove && promotion?.position === position ? promotion : null;
  const fromMoves = selected?.moves ?? [];
  const firstSteps = selected ? chuFirstSteps(fromMoves, selected.from) : [];
  const targets = selected?.via
    ? fromMoves.filter((move) => sameChuSquare(move.via, selected.via)).map((move) => move.to).concat([selected.via], firstSteps)
    : fromMoves.filter((move) => !move.via).map((move) => move.to).concat(firstSteps);
  const moving = selected ? state.board[selected.from[0]]?.[selected.from[1]] : null;
  const selectedCanMove = canMove && moving?.owner === state.side;

  const cancel = () => { setSelection(null); setPromotion(null); };
  const submit = (move: ChuShogiMove) => {
    // 遅れて届いたクリックでも現局面の合法手に一致するものだけ送る。
    const current = legal.find((candidate) => sameChuMove(candidate, move));
    if (canMove && current) onMove?.(current);
    cancel();
  };
  const choose = (moves: ChuShogiMove[]) => {
    if (moves.some((move) => move.promote) && moves.some((move) => !move.promote)) {
      setPromotion({ moves, position });
    } else if (moves[0]) submit(moves[0]);
  };
  const selectPiece = (from: ChuSquare) => {
    const piece = state.board[from[0]]?.[from[1]];
    if (!piece) { cancel(); return; }
    // Read the exact Rust movement rules for either owner. This inspection view
    // never replaces the live legal list used by submit(). A hypothetical turn
    // has no repetition history or opponent's counter-strike restriction.
    const view = piece.owner === state.side && state.outcome == null ? state : {
      ...state, side: piece.owner, outcome: null, position_history: [], move_was_check: [], counter_strike_ban: false,
    };
    let moves: ChuShogiMove[] = [];
    let potentialMoves: ChuShogiMove[] = [];
    try {
      const legalFor = (board: ChuShogiState) => getEngine('ChuShogi').legal(board) as ChuShogiMove[];
      moves = chuMovesFrom(legalFor(view), from);
      potentialMoves = chuPotentialMoves(view, from, legalFor);
    }
    catch { /* A readonly view can precede WASM initialization; retain selection. */ }
    if (canMove && piece.owner === state.side) moves = chuMovesFrom(legal, from);
    setPromotion(null);
    setSelection({ from, via: null, position, moves, potentialMoves });
  };
  const clickSquare = (square: ChuSquare) => {
    if (!canInspect) return;
    if (pending) { cancel(); selectPiece(square); return; }
    // Adjacent lion taps choose a first step; tapping that square again submits
    // the ordinary one-step move. A distant tap remains a direct jump.
    if (selectedCanMove && selected?.via && sameChuSquare(selected.via, square)) {
      const direct = chuMovesTo(legal, selected.from, square);
      if (direct.length) { choose(direct); return; }
    }
    if (selectedCanMove && selected && (selected.via || !firstSteps.some((step) => sameChuSquare(step, square)))) {
      const candidates = chuMovesTo(legal, selected.from, square, selected.via);
      if (candidates.length) { choose(candidates); return; }
    }
    if (selected && !sameChuSquare(selected.from, square) && firstSteps.some((step) => sameChuSquare(step, square))) {
      setSelection({ ...selected, via: square });
      return;
    }
    if (selected && sameChuSquare(selected.from, square)) { cancel(); return; }
    const piece = state.board[square[0]]?.[square[1]];
    if (piece) selectPiece(square);
    else cancel();
  };

  // 後手視点でも自分の駒と文字は正立、相手の駒と文字は一緒に反転。
  const viewer = yourSide ?? 'First';
  const rows = Array.from({ length: CHU_SIZE }, (_, i) => viewer === 'Second' ? i : CHU_SIZE - 1 - i);
  const cols = Array.from({ length: CHU_SIZE }, (_, i) => viewer === 'Second' ? CHU_SIZE - 1 - i : i);
  const showWait = !mini && !staticView && !isMyTurn && state.outcome == null;

  return (
    <div className={`chu-shogi-frame ${mini ? 'mini' : ''} ${showWait ? 'not-my-turn' : ''}`}
      style={{ '--chu-controls-height': `${controlsHeight}px` } as CSSProperties}
      data-testid="chu-shogi-frame" onKeyDown={(event) => { if (event.key === 'Escape') cancel(); }}>
      {!mini && !staticView && !suppressTurnStatus && <div className="chu-shogi-controls" ref={controlsRef}>
        <div className="chu-shogi-help" data-testid="chu-shogi-instruction" role="status">
          {state.outcome ? '終局' : showWait ? '手番待ち' : 'あなたの番'}
          {moving && ` · ${CHU_PIECES[moving.kind].name}${selected?.via ? ' · 2歩目' : selectedCanMove ? '' : ' · 移動範囲'}`}
        </div>
      </div>}
      <div className="chu-shogi-board" data-testid="chu-shogi-board" data-ply={state.ply}
        data-preview-only={selected ? !selectedCanMove : undefined} data-step={selected?.via ? 'second' : 'first'}>
        {!mini && <BoardCoords files={cols.map((col) => String(CHU_SIZE - col))} ranks={rows.map((row) => String(row + 1))} />}
        {rows.flatMap((row) => cols.map((col) => {
          const square: ChuSquare = [row, col];
          const piece = state.board[row]?.[col];
          const movable = canMove && piece?.owner === state.side && legal.some((move) => sameChuSquare(move.from, square));
          const target = targets.some((to) => sameChuSquare(to, square));
          const isSelected = selected && sameChuSquare(selected.from, square);
          const blocked = !target && !isSelected && selected?.potentialMoves.some((move) => sameChuSquare(move.to, square));
          const via = selected?.via && sameChuSquare(selected.via, square);
          const last = !mini && lastMove && [lastMove.from, lastMove.via, lastMove.to].some((sq) => sameChuSquare(sq, square));
          const highlight = tutorialHighlight?.some((sq) => sameChuSquare(sq, square));
          const name = piece ? `${piece.owner === 'First' ? '先手' : '後手'} ${piece.promoted ? '成り' : ''}${CHU_PIECES[piece.kind].name}` : '空きマス';
          return <button key={`${row}-${col}`} type="button" data-testid={`chu-cell-${row}-${col}`}
            data-row={row} data-col={col} data-piece={piece?.kind ?? ''} data-owner={piece?.owner ?? ''} data-blocked={blocked || undefined} data-potential-only={blocked || undefined}
            className={`cell ${movable ? 'movable' : ''} ${target ? selectedCanMove ? 'target' : 'range' : ''} ${blocked ? 'blocked' : ''} ${isSelected ? 'selected' : ''} ${via ? 'via' : ''} ${last ? 'last-move' : ''} ${highlight ? 'tutorial-hint' : ''}`}
            disabled={!canInspect} aria-label={`${chuSquareLabel(square)} ${name}${target ? selectedCanMove ? ' 移動先' : ' 移動範囲' : blocked ? ' 駒なしの移動範囲' : ''}`}
            title={`${chuSquareLabel(square)} ${name}`}
            aria-pressed={Boolean(isSelected)} onClick={() => clickSquare(square)}>
            {piece && <ShogiKoma kanji={piece.kind === 'King' && piece.owner === 'Second' ? '王' : CHU_PIECES[piece.kind].kanji}
              flipped={piece.owner !== viewer} promoted={piece.promoted} />}
          </button>;
        }))}
      </div>

      {pending && <div className="chu-promo-overlay" data-testid="chu-promo-overlay">
        <div className="chu-promo-dialog" data-testid="chu-promo" role="dialog" aria-modal="true" aria-label="成りますか">
          <strong>成りますか</strong>
          <div className="chu-promo-buttons">
            {[true, false].map((promote) => {
              const move = pending.moves.find((candidate) => Boolean(candidate.promote) === promote);
              const piece = move ? state.board[move.from[0]]?.[move.from[1]] : null;
              const kind = piece && promote ? CHU_PROMOTES_TO[piece.kind] ?? piece.kind : piece?.kind;
              return move && <button key={String(promote)} type="button" data-testid={promote ? 'chu-promote' : 'chu-no-promote'}
                onClick={() => submit(move)}>
                {kind && <span className="chu-promo-piece"><ShogiKoma kanji={CHU_PIECES[kind].kanji} promoted={promote} /></span>}
                <span>{promote ? '成る' : '不成'}{kind ? `（${CHU_PIECES[kind].name}）` : ''}</span>
              </button>;
            })}
          </div>
        </div>
      </div>}
    </div>
  );
}
